export function parseUserDate(raw) {
  if (raw == null) return null;

  // 1) Excel serial dates (number or numeric string)
  if (
    (typeof raw === "number" && isFinite(raw)) ||
    (typeof raw === "string" && /^\d+$/.test(raw.trim()))
  ) {
    const n = typeof raw === "number" ? raw : Number(raw.trim());
    if (n > 59) {
      // Excel epoch starts ~ 1900-01-01
      const excelMillis = n * 86400000;
      const base = Date.UTC(1899, 11, 30); // Dec 30, 1899
      return toISOyyyyMMdd(new Date(base + excelMillis));
    }
  }

  // 2) Already a Date
  if (raw instanceof Date && !isNaN(raw.getTime())) {
    return toISOyyyyMMdd(raw);
  }

  if (typeof raw !== "string") return null;

  const s = raw.trim().replace(/\s+/g, " ");
  if (!s) return null;

  // 3) ISO exact: yyyy-mm-dd or yyyy/mm/dd
  let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m) {
    return buildISO(m[1], m[2], m[3]);
  }

  // 4) Day-first numeric: dd-mm-yyyy or dd/mm/yy
  m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2}|\d{4})$/);
  if (m) {
    var dd = m[1],
      mm = m[2],
      yy = m[3];
    var yyyy = normalizeYear(yy);
    return buildISO(yyyy, mm, dd);
  }

  // 5) Compact ddMMyyyy (and ddMMyy)
  m = s.match(/^(\d{2})(\d{2})(\d{2}|\d{4})$/);
  if (m) {
    var dd2 = m[1],
      mm2 = m[2],
      yy2 = m[3];
    var yyyy2 = normalizeYear(yy2);
    return buildISO(yyyy2, mm2, dd2);
  }

  // 6) Day-first with month names: 05-Aug-25, 5 Aug 2025, etc.
  m = s.match(/^(\d{1,2})[ \-\/]([A-Za-z]{3,})[ \-\/](\d{2}|\d{4})$/);
  if (m) {
    var dd3 = m[1],
      monStr = m[2],
      yy3 = m[3];
    var mm3 = monthNameToNumber(monStr);
    if (mm3) {
      var yyyy3 = normalizeYear(yy3);
      return buildISO(yyyy3, String(mm3), dd3);
    }
  }

  // 7) Try swapping separators, still day-first bias
  var cleaned = s.replace(/\./g, "-").replace(/\//g, "-");
  var parts = cleaned.split("-");
  if (
    parts.length === 3 &&
    parts.every(function (p) {
      return /^\d+$/.test(p);
    })
  ) {
    var a = parts[0],
      b = parts[1],
      c = parts[2];
    if (a.length <= 2 && b.length <= 2) {
      return buildISO(normalizeYear(c), b, a);
    }
    if (a.length === 4) {
      return buildISO(a, b, c);
    }
  }

  // 8) Final fallback: native Date
  var fallback = new Date(s);
  if (!isNaN(fallback.getTime())) {
    return toISOyyyyMMdd(fallback);
  }

  return null;
}

// --- helpers ---

function toISOyyyyMMdd(d) {
  var y = d.getUTCFullYear();
  var m = String(d.getUTCMonth() + 1).padStart(2, "0");
  var day = String(d.getUTCDate()).padStart(2, "0");
  return y + "-" + m + "-" + day;
}

function buildISO(y, m, d) {
  var year = Number(y);
  var month = Number(m);
  var day = Number(d);
  if (!isFinite(year) || !isFinite(month) || !isFinite(day)) return null;
  if (month < 1 || month > 12) return null;
  var maxDay = daysInMonth(year, month);
  if (day < 1 || day > maxDay) return null;
  var dt = new Date(Date.UTC(year, month - 1, day));
  return toISOyyyyMMdd(dt);
}

function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function normalizeYear(yy) {
  if (String(yy).length === 2) {
    var n = Number(yy);
    return n <= 68 ? 2000 + n : 1900 + n;
  }
  return Number(yy);
}

function monthNameToNumber(mon) {
  var map = {
    jan: 1,
    january: 1,
    feb: 2,
    february: 2,
    mar: 3,
    march: 3,
    apr: 4,
    april: 4,
    may: 5,
    jun: 6,
    june: 6,
    jul: 7,
    july: 7,
    aug: 8,
    august: 8,
    sep: 9,
    sept: 9,
    september: 9,
    oct: 10,
    october: 10,
    nov: 11,
    november: 11,
    dec: 12,
    december: 12,
  };
  var k = mon.toLowerCase();
  return map[k] || null;
}

export function extractYMD(dateLike) {
  // Preserve the calendar date the user sees in the UI
  if (dateLike instanceof Date && !isNaN(dateLike)) {
    return {
      y: dateLike.getFullYear(),
      m: dateLike.getMonth() + 1,
      d: dateLike.getDate(),
    };
  }
  if (typeof dateLike === "string") {
    const m = dateLike.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return { y: +m[1], m: +m[2], d: +m[3] };
    const d = new Date(dateLike);
    if (!isNaN(d))
      return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() };
  }
  return null;
}

export function excelSerialFromYMD(y, m, d) {
  // Excel epoch with 1900 "leap" bug accounted via Dec 30, 1899
  const ms = Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30);
  return ms / 86400000;
}

export function formatDateTimeForExport(inputdate, { includeMs = false } = {}) {
  const date = new Date(inputdate);
  if (isNaN(date.getTime())) return "-";

  const pad = (n) => (n < 10 ? "0" + n : String(n));
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const min = pad(date.getMinutes());
  const sec = pad(date.getSeconds());

  const time = includeMs
    ? `${hour}:${min}:${sec}:${date.getMilliseconds()}`
    : `${hour}:${min}:${sec}`;

  return `${day}-${month}-${year}, ${time}`;
}
